import type { FormChatState } from "../repositories/formChat.repository";

export interface FinancialEditRecordFields {
  accountId?: string;
  accountName?: string;
  inventoryItemId?: string;
  purchasedQuantity?: number;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === "object" ? (value as Record<string, unknown>) : undefined;

const readId = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) return value.trim();
  const id = asRecord(value)?.id;
  return typeof id === "string" && id.trim() ? id.trim() : undefined;
};

const readName = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/** Purchased quantity is its own field. A generic `quantity` is not this value. */
const readPurchasedQuantity = (value: unknown): number | undefined => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
        ? Number(value)
        : Number.NaN;
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
};

const isUnset = (value: unknown) => value === undefined || value === null || value === "";

/**
 * Saved income and outcome records nest the account under `bankAccount`.
 * The chat form only reads `accountId` and `purchasedQuantity`.
 */
export const readFinancialEditRecord = (data: unknown): FinancialEditRecordFields => {
  const record = asRecord(data);
  if (!record) return {};

  const bankAccount = asRecord(record.bankAccount);
  const account = asRecord(record.account);
  const inventoryItem = asRecord(record.inventoryItem);

  const accountId =
    readId(record.accountId) ??
    readId(record.bankAccountId) ??
    readId(account) ??
    readId(bankAccount);
  const accountName =
    readName(record.accountName) ??
    readName(asRecord(bankAccount?.bankAccountOption)?.name) ??
    readName(bankAccount?.name) ??
    readName(account?.name);
  const inventoryItemId = readId(record.inventoryItemId) ?? readId(inventoryItem);
  const purchasedQuantity = readPurchasedQuantity(record.purchasedQuantity);

  return {
    ...(accountId ? { accountId } : {}),
    ...(accountName ? { accountName } : {}),
    ...(inventoryItemId ? { inventoryItemId } : {}),
    ...(purchasedQuantity ? { purchasedQuantity } : {}),
  };
};

/** Fills gaps from the saved record. Values already in the chat state stay. */
export const withFinancialEditRecord = (
  state: FormChatState,
  record: FinancialEditRecordFields,
): FormChatState => {
  const next: FormChatState = { ...state };
  if (isUnset(next.accountId) && record.accountId) next.accountId = record.accountId;
  if (next.inventoryItemId == null && record.inventoryItemId) {
    next.inventoryItemId = record.inventoryItemId;
  }
  if (isUnset(next.purchasedQuantity) && record.purchasedQuantity) {
    next.purchasedQuantity = record.purchasedQuantity;
  }
  return next;
};
